// The conditions of an own fleet as chips (v2 slot 1; v2-the-deep.md §6.1,
// §18.2; ui-map-interface.md §8 "The conditions row"; mockup round 088
// card 02 a).
//
// The wire sends one `Condition` for each stack key that acts on an own
// fleet now, in the order of `ConditionKey` (protocol 38, R2576). This
// module turns that list into the `Cond[]` of `conds.ts`: the glyph and
// the tone of the key from `[presentation.conditions]` (`CONDITIONS`), the
// cap word from `cond_<key>` of `[ui.card]`, and the figure from `pct` and
// `left_sec`. The `figure` of the row says how `pct` reads (R2616): a
// factor (`×0.6`), a share of the hull speed (DRIFT, `30 %`, R2609), or
// the hull a minute that pressure costs, which the caller hands in from
// the rules of the catalog (PRESSURE states no stat), or the points the
// command aura lifts, which the caller hands in from the captains (COMMAND,
// R2732). A key the table does not know draws no chip, so a newer
// server never breaks the card. A contact carries no conditions, so its
// card has no row.
//
// Everything here is pure. The caller passes the form of a map time.

import type { Condition } from '../../gen/proto/aquaphobia';
import { CONDITIONS } from '../../gen/presentation';
import { card as S } from '../../gen/strings';
import { wordOf } from '../../locale/wordof';
import type { Cond } from './conds';

/** The factor of a stat in percent as the chip writes it: 60 is `×0.6`,
 *  125 is `×1.25`. */
export function factorText(pct: number): string {
  return S.cond_factor({ f: String(Math.round(pct) / 100) });
}

/** The hull a minute that pressure costs, as the chip writes it: one place
 *  under ten, whole figures from ten. */
export function wearText(n: number): string {
  return S.cond_wear({ n: String(n >= 10 ? Math.round(n) : Math.round(n * 10) / 10) });
}

/** What the caller knows that the wire does not: the hull a minute that
 *  pressure costs this hull, or 0 when the figure is unknown (R2616); the
 *  points the command aura lifts and their cause, for the chip's figure
 *  and title (R2732). */
export interface CondOpts { wearPerMin?: number; aura?: { value: string; why: string } }

/** The figure of one condition by the `figure` of its row. */
function figureOf(row: (typeof CONDITIONS)[number], c: Condition, o: CondOpts): string {
  switch (row.figure) {
    case 'share': return c.pct > 0 ? S.cond_share({ pct: Math.round(c.pct) }) : '';
    case 'wear': return (o.wearPerMin ?? 0) > 0 ? wearText(o.wearPerMin!) : '';
    case 'aura': return o.aura?.value ?? '';
    default: return c.pct > 0 ? factorText(c.pct) : '';
  }
}

/**
 * The chips of one fleet's conditions, in the order of the list. `left`
 * writes the map seconds left on a timed condition; a condition with no
 * figure and no time left is the cap word alone.
 */
export function condsOf(
  list: readonly Condition[] | undefined, left: (mapSec: number) => string, o: CondOpts = {},
): Cond[] {
  const out: Cond[] = [];
  if (!list) return out;
  for (const c of list) {
    const row = CONDITIONS[c.key];
    if (!row) continue;
    const factor = figureOf(row, c, o);
    const time = c.leftSec > 0 ? left(c.leftSec) : '';
    const value = factor && time ? S.cond_value({ factor, left: time }) : factor || time;
    const cond: Cond = {
      key: row.key, label: wordOf(S, `cond_${row.key}`, row.key.toUpperCase()),
      tone: row.tone, icon: row.icon,
    };
    if (value) cond.value = value;
    if (row.figure === 'aura' && o.aura?.why) cond.why = o.aura.why;
    if (row.hull) cond.hull = true;
    out.push(cond);
  }
  return out;
}
